// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import * as InviteCommands from '@app/features/invite/commands/InviteCommands';
import * as InviteUtils from '@app/features/invite/utils/InviteUtils';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {SendInviteToCommunityIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {
	beginInviteToCommunityGuard,
	getInviteToCommunityGuardKey,
	scheduleInviteToCommunityGuardRelease,
} from '@app/features/ui/action_menu/items/InviteToCommunityGuard';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {User} from '@app/features/user/models/User';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import type {Invite} from '@app/vendor/schema/domains/invite/InviteSchemas';
import {fromTimestamp} from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';

const logger = new Logger('InviteMenuItems');
interface InviteCandidate {
	guild: Guild;
	channelId: string;
}
const canInviteInChannel = (channel: Channel | undefined): channel is Channel => {
	if (!channel || !channel.guildId) {
		return false;
	}
	return InviteUtils.canInviteToChannel(channel.id, channel.guildId);
};
const getDefaultInviteChannelId = (guildId: string): string | null => {
	const selectedChannelId = SelectedChannel.selectedChannelIds.get(guildId);
	if (selectedChannelId) {
		const selectedChannel = Channels.getChannel(selectedChannelId);
		if (canInviteInChannel(selectedChannel)) {
			return selectedChannel!.id;
		}
	}
	const guildChannels = Channels.getGuildChannels(guildId);
	for (const channel of guildChannels) {
		if (channel.type === ChannelTypes.GUILD_TEXT && canInviteInChannel(channel)) {
			return channel.id;
		}
	}
	return null;
};
interface InviteToCommunityMenuItemProps {
	user: User;
	onClose: () => void;
}
export const InviteToCommunityMenuItem: React.FC<InviteToCommunityMenuItemProps> = observer(({user, onClose}) => {
	const {t} = useLingui();
	const sendingRef = useRef(false);
	const [sendingGuildId, setSendingGuildId] = useState<string | null>(null);
	const candidates = useMemo(() => {
		return Guilds.getGuilds()
			.filter((guild) => !GuildMembers.getMember(guild.id, user.id))
			.map((guild): InviteCandidate | null => {
				const channelId = getDefaultInviteChannelId(guild.id);
				return channelId ? {guild, channelId} : null;
			})
			.filter((candidate): candidate is InviteCandidate => candidate !== null)
			.sort((a, b) => a.guild.name.localeCompare(b.guild.name));
	}, [user.id]);
	const handleSendInvite = useCallback(
		async (candidate: InviteCandidate) => {
			if (sendingRef.current) return;
			const guardKey = getInviteToCommunityGuardKey(user.id, candidate.guild.id, candidate.channelId);
			if (!beginInviteToCommunityGuard(guardKey)) return;
			sendingRef.current = true;
			setSendingGuildId(candidate.guild.id);
			try {
				let invite: Invite;
				try {
					invite = await InviteCommands.create(candidate.channelId);
				} catch {
					return;
				}
				const inviteUrl = `${RuntimeConfig.inviteEndpoint}/${invite.code}`;
				const dmChannelId = await PrivateChannelCommands.ensureDMChannel(user.id);
				try {
					const result = await MessageCommands.send(dmChannelId, {
						content: inviteUrl,
						nonce: fromTimestamp(Date.now()),
					});
					if (result) {
						ToastCommands.createToast({
							type: 'success',
							children: <Trans>Invite sent for {candidate.guild.name}</Trans>,
						});
					}
				} catch (error) {
					logger.error('Failed to send invite via context menu:', error);
					ToastCommands.error(t`Failed to send invite. Please try again.`);
				} finally {
					onClose();
				}
			} finally {
				sendingRef.current = false;
				setSendingGuildId(null);
				scheduleInviteToCommunityGuardRelease(guardKey);
			}
		},
		[onClose, user.id],
	);
	if (user.bot || candidates.length === 0) {
		return null;
	}
	return (
		<MenuItemSubmenu
			label={t`Invite to Community`}
			render={() => (
				<MenuGroup>
					{candidates.map((candidate) => (
						<MenuItem
							key={candidate.guild.id}
							icon={<SendInviteToCommunityIcon />}
							onClick={() => handleSendInvite(candidate)}
							disabled={sendingGuildId !== null}
							closeOnSelect={false}
						>
							{candidate.guild.name}
						</MenuItem>
					))}
				</MenuGroup>
			)}
		/>
	);
});
